import { FC, useCallback, useMemo, useState } from 'react';

import {
  DELIVERY_CHANNEL_CONFIG,
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
  GiftCouponForRedemption,
  formatDeliveryChannelSelectLabel,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import {
  formatDateTime,
  formatPhone,
  getChoiceUnitName,
  getItemName,
} from '../utils/redemption.utils';

interface Props {
  todayDelivered: GiftCouponForRedemption[];
  showTodayDelivered: boolean;
  setShowTodayDelivered: (fn: (prev: boolean) => boolean) => void;
  copyToClipboard: (text: string) => void;
  onBulkUpdateDeliveryChannel: (
    couponIds: string[],
    newChannel: DeliveryChannel | '',
  ) => Promise<void>;
}

const CHANNEL_OPTIONS: Array<{ value: DeliveryChannel | ''; label: string }> = [
  ...DELIVERY_CHANNEL_LIST.map((ch) => ({
    value: ch,
    label: formatDeliveryChannelSelectLabel(ch),
  })),
  { value: '', label: '입력안함' },
];

export const TodayDeliveredTable: FC<Props> = ({
  todayDelivered,
  showTodayDelivered,
  setShowTodayDelivered,
  copyToClipboard,
  onBulkUpdateDeliveryChannel,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editChannel, setEditChannel] = useState<DeliveryChannel | ''>('');
  const [isUpdating, setIsUpdating] = useState(false);

  const groupMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const coupon of todayDelivered) {
      const key = formatDateTime(coupon.gifticon?.deliveredAt, true);
      const ids = map.get(key);
      if (ids) {
        ids.push(coupon._id);
      } else {
        map.set(key, [coupon._id]);
      }
    }
    return map;
  }, [todayDelivered]);

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleGroup = useCallback(
    (groupKey: string) => {
      const ids = groupMap.get(groupKey);
      if (!ids) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const allSelected = ids.every((id) => next.has(id));
        if (allSelected) {
          ids.forEach((id) => next.delete(id));
        } else {
          ids.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [groupMap],
  );

  const handleApply = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setIsUpdating(true);
    try {
      await onBulkUpdateDeliveryChannel(ids, editChannel);
      setSelectedIds(new Set());
    } finally {
      setIsUpdating(false);
    }
  };

  const channelCounts = useMemo(() => {
    const counts = new Map<string, { label: string; badgeClass: string; count: number }>();
    for (const coupon of todayDelivered) {
      const ch = coupon.gifticon?.deliveryChannel;
      if (!ch) continue;
      const existing = counts.get(ch);
      if (existing) {
        existing.count++;
      } else {
        const config = DELIVERY_CHANNEL_CONFIG[ch];
        counts.set(ch, { label: config.label, badgeClass: config.badgeClass, count: 1 });
      }
    }
    return Array.from(counts.values()).sort((a, b) => b.count - a.count);
  }, [todayDelivered]);

  if (todayDelivered.length === 0) return null;

  return (
    <div className="px-4 pb-2">
      <button
        onClick={() => setShowTodayDelivered((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg bg-green-50 px-4 py-3 text-left transition-colors hover:bg-green-100"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-green-800">
            오늘 발송 완료 ({todayDelivered.length}건)
          </span>
          {channelCounts.map((ch) => (
            <span
              key={ch.label}
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${ch.badgeClass}`}
            >
              {ch.label} {ch.count}
            </span>
          ))}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-green-600 transition-transform ${
            showTodayDelivered ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {showTodayDelivered && (
        <>
          {/* 선택 시 매체 변경 바 */}
          {selectedIds.size > 0 && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-indigo-50 px-4 py-2">
              <span className="text-xs font-medium text-indigo-700">
                {selectedIds.size}건 선택
              </span>
              <select
                value={editChannel}
                onChange={(e) => setEditChannel(e.target.value as DeliveryChannel | '')}
                disabled={isUpdating}
                className="rounded border border-indigo-300 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none"
              >
                {CHANNEL_OPTIONS.map((opt) => (
                  <option
                    key={opt.value === '' ? '__none__' : opt.value}
                    value={opt.value}
                  >
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                onClick={handleApply}
                disabled={isUpdating}
                className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {isUpdating ? '처리중...' : '매체 변경'}
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                disabled={isUpdating}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                선택 해제
              </button>
            </div>
          )}

          <div className="mt-2 overflow-x-auto rounded-lg bg-white shadow">
            <table className="min-w-full">
              <thead className="bg-green-50">
                <tr>
                  <th className="w-8 px-2 py-2" />
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    쿠폰번호
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    _id
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    상품
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    메모
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    확인번호
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    수신자
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-green-700">
                    도매가
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    신청시각
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    발송시각
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                    매체
                  </th>
                </tr>
              </thead>
              <tbody>
                {todayDelivered.map((coupon, idx) => {
                  const curKey = formatDateTime(coupon.gifticon?.deliveredAt, true);
                  const prevKey =
                    idx > 0
                      ? formatDateTime(
                          todayDelivered[idx - 1]?.gifticon?.deliveredAt,
                          true,
                        )
                      : null;
                  const isNewGroup = idx === 0 || curKey !== prevKey;
                  const choiceUnitName = getChoiceUnitName(coupon);
                  const groupIds = groupMap.get(curKey) ?? [];
                  const isGroupAllSelected =
                    groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));

                  const channelConfig = coupon.gifticon?.deliveryChannel
                    ? DELIVERY_CHANNEL_CONFIG[coupon.gifticon.deliveryChannel]
                    : null;

                  return (
                    <tr
                      key={coupon._id}
                      className={`hover:bg-gray-50 ${
                        selectedIds.has(coupon._id) ? 'bg-indigo-50/40' : ''
                      } ${isNewGroup && idx > 0 ? 'border-t-2 border-green-200' : ''}`}
                    >
                      <td className="px-2 py-2 text-center">
                        {isNewGroup ? (
                          <input
                            type="checkbox"
                            checked={isGroupAllSelected}
                            onChange={() => toggleGroup(curKey)}
                            title={`묶음 일괄선택 (${groupIds.length}건)`}
                            className="h-3.5 w-3.5 cursor-pointer rounded border-indigo-400 text-indigo-600 accent-indigo-600"
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(coupon._id)}
                            onChange={() => toggleOne(coupon._id)}
                            className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-indigo-600 accent-indigo-600"
                          />
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-700">
                        {coupon.fullCode || '-'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2">
                        <button
                          onClick={() => copyToClipboard(coupon._id)}
                          className="font-mono text-xs text-gray-400 hover:text-indigo-600"
                          title="클릭하여 _id 복사"
                        >
                          {coupon._id.slice(-6)}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-900">
                        {getItemName(coupon)}
                        {choiceUnitName && (
                          <span className="ml-1 text-indigo-500">({choiceUnitName})</span>
                        )}
                      </td>
                      <td className="max-w-[120px] px-4 py-2">
                        {coupon.memo ? (
                          <p
                            className="truncate text-xs text-gray-600"
                            title={coupon.memo}
                          >
                            {coupon.memo}
                          </p>
                        ) : (
                          <span className="text-[10px] text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-amber-600">
                        {coupon.additionalRecipientPhone ? (
                          formatPhone(coupon.additionalRecipientPhone)
                        ) : (
                          <span className="text-[10px] text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-700">
                        {formatPhone(coupon.recipient?.phoneNumber)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-indigo-600">
                        {coupon.gifticon?.priceWhole != null
                          ? `${coupon.gifticon.priceWhole.toLocaleString()}원`
                          : '-'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                        {formatDateTime(coupon.recipient?.requestedAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                        {formatDateTime(coupon.gifticon?.deliveredAt, true)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs">
                        {channelConfig ? (
                          <span
                            className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${channelConfig.badgeClass}`}
                          >
                            {channelConfig.label}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
